import { Component, Vue, Watch } from 'vue-property-decorator';
import { isEqual } from 'lodash';

import AnalysisStep from '@/components/analysis/steps/AnalysisStep.vue';
import AnalysisStepAnalysisTimer from '@/components/analysis/steps/analysis/AnalysisStepAnalysisTimer.vue';

import { StepsKeys } from '@/plugins/podolib/services/analysis';
import {
  AnalysisRunningMode,
  AnalysisWalkingMode,
  AnalysisType,
} from '@/components/analysis/steps/type/AnalysisStepType';

export type StepAnalysis = {
  delay: number;
  duration: number | null;
  speed: number | null;
  observation: string;
  flags: {
    analysis: {
      isInternal: boolean;
      isPending: boolean;
      waitStarted: boolean;
      isStarted: boolean;
      waitGettingData: boolean;
      isGettingData: boolean;
      waitCancelled: boolean;
      isCancelled: boolean;
      isFailure: boolean;
      isSuccess: boolean;
    };
    compute: {
      isComputing: boolean;
      isSuccess: boolean;
      isFailure: boolean;
    };
  };
  timer: {
    startDate: any | null;
    endDate: any | null;
    current: any | null;
    completed: boolean;
  };
  warn: Error | null;
  error: Error | null;
  acquisitionId: string | null;
};

@Component({
  components: {
    AnalysisStep,
    AnalysisStepAnalysisTimer,
  },
})
export default class AnalysisStepAnalysis extends Vue {
  // Main data payload
  data: StepAnalysis = null!;

  /**
   * Helper for giving ability to use types and enums in DOM
   */
  tsdom = {
    Type: AnalysisType,
    RunningMode: AnalysisRunningMode,
    WalkingMode: AnalysisWalkingMode,
  };

  // Research params for the 10m distance
  tenMetersMode = false;
  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Get manifest entry
   */
  get stepAnalysis() {
    return this.$pdc.analysis!.getManifestEntry(StepsKeys.Analysis);
  }

  /**
   * Manifest entry of "type" step
   */
  get stepType() {
    return this.$pdc.analysis!.getManifestEntry(StepsKeys.Type);
  }

  /**
   * Manifest entry of "station" step
   */
  get stepStation() {
    return this.$pdc.analysis!.getManifestEntry(StepsKeys.Station);
  }

  /**
   * Manifest entry of "solesPair" step
   */
  get stepSolesPair() {
    return this.$pdc.analysis!.getManifestEntry(StepsKeys.SolesPair);
  }

  /**
   * Get 'patient' entity
   */
  get patient() {
    return this.$pdc.analysis!.entityPatient;
  }

  /**
   * Get 'command' entity
   */
  get command() {
    return this.$pdc.analysis!.entityCommand;
  }

  /**
   * Get step title
   */
  get title() {
    // Error
    if (this.data.error) {
      return this.$t('analysis.steps.analysis.title.error');
    }
    // Warn
    else if (this.data.warn) {
      return this.$t('analysis.steps.analysis.title.warn');
    }
    // Analysis 'isPending'
    else if (this.data.flags.analysis.isPending) {
      return this.$t('analysis.steps.analysis.title.is-pending');
    }
    // Analysis 'isStarted'
    else if (this.data.flags.analysis.isStarted) {
      return this.$t('analysis.steps.analysis.title.is-started');
    }
    // Analysis 'isStopped'
    else if (this.data.flags.analysis.isGettingData) {
      return this.$t('analysis.steps.analysis.title.is-getting-data');
    }
    // Compute 'isComputing'
    else if (this.data.flags.compute.isComputing) {
      return this.$t('analysis.steps.analysis.compute.computing');
    }
    // Compute 'isFailure'
    else if (this.data.flags.compute.isFailure) {
      return this.$t('analysis.steps.analysis.compute.failure');
    }
    // Compute 'isSuccess'
    else if (this.data.flags.compute.isSuccess) {
      return this.$t('analysis.steps.analysis.compute.success');
    }
    // Default
    else {
      return this.$t('analysis.steps.analysis.title.ready');
    }
  }

  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Keep manifest up to date
   */
  @Watch('data', { deep: true })
  onDataChanged(newValue: StepAnalysis) {
    if (!isEqual(newValue, this.stepAnalysis)) {
      this.$pdc.analysis!.setManifestEntry(StepsKeys.Analysis, this.data);
    }

    this.$nextTick(async () => {
      if (newValue.flags.compute.isSuccess) {
        await this.goToAcquisition();
      }
    });
  }

  /**
   * Keep local data up to date
   */
  @Watch('stepAnalysis', { deep: true, immediate: true })
  onStepComputeChanged(newValue: StepAnalysis) {
    if (!isEqual(newValue, this.data)) {
      this.data = { ...this.data, ...this.stepAnalysis };
    }
  }

  // -------------------------------------------------------------------------------------------------------------------

  reduceDuration() {
    if (this.data.duration === null) return;

    if (this.data.duration <= 30000) {
      this.data.speed = 30000;
    } else {
      this.data.duration -= 30000;
    }
  }

  increaseDuration() {
    if (this.data.duration === null) return;

    if (this.data.duration >= 600000) {
      this.data.duration = 600000;
    } else {
      this.data.duration += 30000;
    }
  }

  reduceSpeed() {
    if (this.data.speed === null) return;

    if (this.data.speed <= 5) {
      this.data.speed = 5;
    } else {
      this.data.speed -= 1;
    }
  }

  increaseSpeed() {
    if (this.data.speed === null) return;

    if (this.data.speed >= 25) {
      this.data.speed = 25;
    } else {
      this.data.speed += 1;
    }
  }

  selectTenMetersMode() {
    this.tenMetersMode = !this.tenMetersMode;
    // Disabled duration if tenMetersMode is active
    this.tenMetersMode ? (this.data.duration = null) : (this.data.duration = 120000);
  }

  /**
   * Start an analysis
   */
  async startAnalysis() {
    try {
      await this.$pdc.analysis!.startAnalysis();

      // Analytics
      this.$pdc.analytics?.actions.trackSuccess('NewAnalysis Started', {
        type: this.stepType.type,
        mode: this.stepType.mode,
        duration: this.stepAnalysis.duration,
        speed: this.stepAnalysis.speed,
      });
    } catch (e) {
      // Analytics
      this.$pdc.analytics?.actions.trackFailure('NewAnalysis Started', e, {
        type: this.stepType.type,
        mode: this.stepType.mode,
        duration: this.stepAnalysis.duration,
        speed: this.stepAnalysis.speed,
      });
    }
  }

  /**
   * Stop an analysis
   */
  async stopAnalysis() {
    try {
      await this.$pdc.analysis!.stopAnalysis();

      // Analytics
      this.$pdc.analytics?.actions.trackSuccess('NewAnalysis Stopped');
    } catch (e) {
      // Analytics
      this.$pdc.analytics?.actions.trackFailure('NewAnalysis Stopped', e);
    }
  }

  /**
   * Cancel an analysis
   */
  async cancelAnalysis() {
    try {
      await this.$pdc.analysis!.cancelAnalysis();

      // Analytics
      this.$pdc.analytics?.actions.trackSuccess('NewAnalysis Cancelled');
    } catch (e) {
      // Analytics
      this.$pdc.analytics?.actions.trackFailure('NewAnalysis Cancelled', e);
    }
  }

  async goToAcquisition() {
    await this.$router.push({ path: `/patient/${this.patient!._id}/result/${this.data.acquisitionId}` });
    await this.$pdc.analysis!.cancelAnalysis();
    this.$pdc.analysis!.clean(true);
  }

  // -------------------------------------------------------------------------------------------------------------------

  /**
   * Emit "next" event to the parent
   */
  next() {
    this.$emit('next');
  }
}
